import React, {useEffect, useState} from 'react';
import {useRequest} from 'ahooks';
import classnames from 'classnames';
import {history} from 'umi';
import {SortableColumn, SortBy} from 'tea-component/lib/table/addons/sortable';
import {Bubble, Button, ConfigProvider, PagingQuery, Table, TableColumn, Text} from 'tea-component';
import {
  getNewDataTable,
  getTableFilterOptions,
  getTableScheme,
} from '@dbl/services/dataSource';

import Loading from '@/pages/datalab/components/Loading';
import DataTableDetail from './DataTableDetail';

import {COMMON_BUSINESS_NAME, DATALAB_EDITOR, SYSTEM_NAME} from '@dbl/core/global';
import {getLocalText, updateUrlParams, useDblIntl} from '@/pages/datalab/utils/utils';
import {AuthPermissionDisablesContainers} from '../../../WorkBench/MyDashboard';
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import './DataTable.scss';
import {TableHoverFiledSearchBox} from '../../../Home/RecentlyAndShare';

// 表格分页
interface PageType {
  pageSize: number;
  pageIndex: number;
}

// 上传参数请求
interface TableRequestParams {
  game_code: string; // 游戏编码
  first_labels: string[]; // 一级类目
  second_labels: string[]; // 二级类目
  drivers: string[]; // 数据库
  db_name_filters: Array<{ db_name: string; schema: string; }>;
  cycles: string[]; // 表周期
  keywords: string | undefined; // 搜索内容
  order_by: string; // 排序字段, key值
  desc: boolean; // true是倒序, false是正序
  page: number; // 分页页码
  page_size: number; // 每页条数
}

interface IQuickFilterValue {
  drivers: string[];
  db_name_filters: string[];
}

interface ISelectTableItem {
  table_id: string;
  database_uuid?: string;
  db_name?: string;
}

interface IFilterValue {
  label: string[];
  sub_label: string[];
  cycle: string[];
}

type TTranslatorMap = Record<string, Record<string, { en?: string; zh?: string; }>>;

// 表格Props
interface DataTableProps {
  gameCode: string;
  searchWords?: string | undefined;
  selectTables: ISelectTableItem[];
  setSelectTables: Function;
  clearSearchWords: () => void;
  className: string;
  filterValue: IFilterValue;
  translatorMap: TTranslatorMap;
  hasSpecifiedPrivilege: (privilege: string) => boolean;
}

// 排序列定义
const sortColumns: SortableColumn[] = [
  { key: 'table_name', prefer: 'asc' },
  { key: 'label', prefer: 'asc' },
  { key: 'sub_label', prefer: 'asc' },
  { key: 'create_time_str', prefer: 'asc' },
  { key: 'use_count', prefer: 'desc' },
];

const { pageable, rowtooltip, scrollable, sortable, autotip } = Table.addons;
function DataTable(props: DataTableProps) {
  const { dblFormatMessage: dataFormatMessage, commonFormatMessage, isEn } = useDblIntl('data_table');
  const { selectTables, setSelectTables, filterValue, translatorMap, className, gameCode } = props;
  const {query} = history.location;
  const [records, setRecords] = useState<any[]>([]);
  const [sort, setSort] = useState<SortBy>({ by: 'use_count', order: 'desc' });
  // 分页信息
  const [pageInfo, setPageInfo] = useState<PageType>({ pageSize: 10, pageIndex: 1 });
  // 总条数
  const [total, setTotal] = useState<number>(0);
  // 当前表周期
  // const [curCycle, setCurCycle] = useState<string>('');
  
  const [showTableDetail, setShowTableDetail] = useState<boolean>(false);
  const [tableDetailId, setTableDetailId] = useState<string>('');
  const [tableDetailName, setTableDetailName] = useState<string>('');
  const [quickValue, setQuickValue] = useState<IQuickFilterValue>({
    drivers: [],
    db_name_filters: [],
  });
  const [tableFilterOptions, setTableFilterOptions] = useState<{
    drivers: Array<{ value: string; }>;
    db_names: Array<{ db_name: string; schema: string; }>;
  }>({
    drivers: [],
    db_names: [],
  });
  
  const { loading, error, runAsync } = useRequest(getNewDataTable, {
    manual: true,
  }); // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求
  
  const { loading: runGetTableSchemeLoading } = useRequest(getTableScheme, {
    manual: true,
  });

  const getDbNameFilterKey = (item: { db_name: string; schema: string; }): string => {
    return `${item?.db_name || ''}__${item?.schema || ''}`;
  };

  const parseDbNameFilterValue = (value: string): { db_name: string; schema: string; } => {
    const [dbName = '', schema = ''] = String(value || '').split('__');
    return { db_name: dbName, schema };
  };
  
  // 根据后端返回的中英映射, 获取文本
  const getText = (key: string, value: string) : string => {
    const map = translatorMap?.[key]?.[value];
    return map?.[isEn ? 'en' : 'zh'] || value || "-";
  };

  const driverFilterData = tableFilterOptions?.drivers?.map((item) => {
    const driver = item?.value || '';
    return {
      value: driver,
      text: getText('driver', driver),
    };
  }) || [];

  const dbNameFilterData = tableFilterOptions?.db_names?.map((item) => {
    const key = getDbNameFilterKey(item);
    return {
      value: key,
      text: `${item?.db_name || ''}${item?.schema ? `(${item.schema})` : ''}`,
    };
  }) || [];

  const handleOnClickCreateSearch = (key: keyof IQuickFilterValue, value: string[]) => {
    const nextQuickValue = {
      ...quickValue,
      [key]: value || [],
    };
    requestDataTable({ pageSize: pageInfo?.pageSize, pageIndex: 1 }, sort, nextQuickValue);
  };

  const requestFilterOptions = (): void => {
    const params = {
      game_code: props?.gameCode,
      first_labels: filterValue?.label?.includes('all') ? [] : filterValue?.label || [],
      second_labels: filterValue?.sub_label?.includes('all') ? [] : filterValue?.sub_label || [],
      drivers: [],
      db_name_filters: [],
      cycles: filterValue?.cycle?.includes('all') ? [] : filterValue?.cycle || [],
      keywords: "",
    };
    getTableFilterOptions(params)
      .then((res: any) => {
        setTableFilterOptions({
          drivers: res?.drivers || [],
          db_names: res?.db_names || [],
        });
      })
      .catch((e) => {
        console.log('数据表管理页面 过滤项请求错误: ', e);
      });
  };
  
  // 列定义
  const columns: TableColumn[] = [
    {
      key: 'table_name',
      header: dataFormatMessage('name'),
      width: 400,
      fixed: 'left',
      render: (record: any) => {
        const isInclude = selectTables?.some((item) => item.table_id === record.table_id);
        const notSameDatabase = Boolean(
          selectTables[0]?.db_name &&
            (record.db_name !== selectTables[0]?.db_name),
        );
        const name = getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
        return (
          <div className="flex-c">
            <Bubble
              dark
              content={(<>{name}<br/>{dataFormatMessage('size')}: {record.capacity}</>)}
              placement={'top'}
            >
              <Text style={{ maxWidth: 335 }} overflow>
                <Bubble
                  dark
                  content={
                    // eslint-disable-next-line no-nested-ternary
                    notSameDatabase ? dataFormatMessage('not_same') :
                      (selectTables.length >= 3 && !isInclude && record.sync_status !== 1
                        ? dataFormatMessage('more_three')
                        : '')
                  }
                  placement={'top'}
                >
                  <input
                    type="checkbox"
                    checked={isInclude}
                    disabled={
                      (selectTables.length >= 3 && !isInclude) || record.sync_status === 1 || notSameDatabase
                    }
                    style={{ marginRight: '10px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectTables((prevSelectTables: ISelectTableItem[]) => {
                        if (isInclude) {
                          return prevSelectTables?.filter((item) => item.table_id !== record.table_id) || prevSelectTables;
                        }
                        return [...prevSelectTables, {table_id: record.table_id, database_uuid: record.database_uuid, db_name: record.db_name}];
                      });
                    }}
                    onChange={() => {
                      // 避免控制台出现红色Waring
                    }}
                  />
                </Bubble>
                {name}
              </Text>
            </Bubble>
            
            {/* {
              (record.scheme && record.sync_status !== 1) ? (<Icon
                type="externallink"
                onClick={(e) => {
                  e.stopPropagation();
                  runGetTableScheme({
                    game_code: props?.gameCode,
                    table_names: [record.table_name],
                  })
                    .then((res) => {
                      const scheme = res?.table_list?.[0]?.scheme;
                      if (scheme) {
                        window.open(`${window.location.origin}${scheme}`);
                      }
                    })
                    .catch(() => {});
                }}
              />) : ''
            } */}
          
          </div>
        );
      },
    },
    {
      key: 'table_desc',
      header: dataFormatMessage('description'),
      width: 350,
      render: (record: any) => {
        const desc = (isEn ? record.table_desc_en || record.table_desc : record.table_desc);
        return desc ? (
          <BubbleEllipsis content={desc} className={'data-management-table-desc-bubble'}>
            <Text style={{width: '100%' }} overflow>
              {desc}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: 'use_count',
      header: dataFormatMessage('usage_count'),
      width: isEn? 160 : 130,
      align: 'right',
    },
    {
      key: 'label',
      header: dataFormatMessage('level_one'),
      width: isEn ? 214 : 150,
      render(record) {
        return getText('label', record?.label);
      },
    },
    {
      key: 'sub_label',
      header: dataFormatMessage('level_two'),
      width: isEn ? 214 : 150,
      render(record) {
        return getText('sub_label', record?.sub_label);
      },
    },
    {
      key: 'cycle',
      header: dataFormatMessage('cycle'),
      width: 150,
      render(record) {
        return getText('cycle', record?.cycle);
      },
    },
    {
      key: 'driver',
      header: <div key={JSON.stringify(quickValue?.drivers)}>
        <TableHoverFiledSearchBox
          {...{
            data: driverFilterData,
            handleOnClickCreateSearch: (value: string[]) =>
              handleOnClickCreateSearch('drivers', value),
            value: quickValue?.drivers,
            buttonText: dataFormatMessage('database_type'),
          }}
        />
      </div>,
      width: 170,
      align: 'left',
      render(record) {
        return getText('driver', record?.driver);
      },
    },
    {
      key: 'db_name',
      header: <div key={JSON.stringify(quickValue?.db_name_filters)}>
        <TableHoverFiledSearchBox
          {...{
            data: dbNameFilterData,
            handleOnClickCreateSearch: (value: string[]) =>
              handleOnClickCreateSearch('db_name_filters', value),
            value: quickValue?.db_name_filters,
            buttonText: dataFormatMessage('database_name'),
            listWidth: 300,
          }}
        />
      </div>,
      width: 260,
      align: 'left',
      render: (record) => {
        const content = `${record.db_name}${ (record.scheme && (typeof record.scheme === 'string')) ? `(${record.scheme})` : ''  }`;
        return content ? (<BubbleEllipsis
          content={content}
          className={'add-source-table-desc-bubble'}
        />) : '-';
      }
    },
    {
      key: 'creator',
      header: dataFormatMessage('person_in_charge'),
      width: isEn ? 190 : 162,
    },
    {
      key: 'create_time_str',
      header: dataFormatMessage('creation_time'),
      width: 190,
    },
    {
      key: 'handle',
      header: dataFormatMessage('operation'),
      width: isEn ? 210 : 180,
      render: (record: any) => (
        <>
          {
            <AuthPermissionDisablesContainers  isShow type={DATALAB_EDITOR.AddChartBtn} customDisabled={record?.permission !== '1' || record?.sync_status === 1}>
              <Button
                className="table-btn"
                type="link"
                onClick={() => {
                  history.push(`/${SYSTEM_NAME}/${props?.gameCode}/Analysis/MultiAnalysis?tableId=${record.table_id}`);
                }}
              >
                {dataFormatMessage('analysis')}
              </Button>
            </AuthPermissionDisablesContainers>
          }
          <AuthPermissionDisablesContainers  isShow type={record.game_code === COMMON_BUSINESS_NAME ? DATALAB_EDITOR.Edit_Common_DataSet : DATALAB_EDITOR.Edit_DataSet} customDisabled={record?.permission !== '1' || record?.sync_status === 1}>
            <Button
              className="table-btn"
              type="link"
              onClick={() => {
                history.push(`/${SYSTEM_NAME}/${props?.gameCode}/DataManagement/SheetManagement/Edit?tableId=${record.table_id}&tableName=${record.table_name}`);
              }}
            >
              {dataFormatMessage('edit')}
            </Button>
          </AuthPermissionDisablesContainers>
          <Button
            className="table-btn"
            type="link"
            disabled={record?.sync_status === 1}
            onClick={() => {
              const name = getLocalText(isEn, record?.customize_name, record?.customize_name_en, record?.table_name);
              setTableDetailId(record.table_id);
              setTableDetailName(name);
              setShowTableDetail(true);
            }}
          >
            {dataFormatMessage('details')}
          </Button>
        </>
      ),
      fixed: 'right',
    },
  ];
  // 发起表格请求（列头过滤器清理由 FilterTabs / 搜索区等事件内 updateUrlParams 写入 URL）
  useEffect(() => {
    const newPageInfo = typeof query?.pageInfo === 'string'
      ? JSON.parse(query.pageInfo)
      : pageInfo;
    let newSort: SortBy = typeof query?.sort === 'string'
      ? JSON.parse(query.sort)
      : sort;
    const newQuickValue: IQuickFilterValue = query?.quickValue
      ? JSON.parse(query?.quickValue as string)
      : quickValue;
    // 搜索状态下切换筛选Tab，重置排序为默认（无排序列指示器，不传order_by）
    if (props?.searchWords) {
      newSort = {};
    }
    if (pageInfo.pageIndex === newPageInfo?.pageIndex) {
      newPageInfo.pageIndex = 1;
    }
    requestDataTable(newPageInfo, newSort, newQuickValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.searchWords, filterValue]);

  useEffect(() => {
    requestFilterOptions();
  }, [filterValue]);
  /**
   * 表格请求
   * @param pageInfo 分页信息, 主要用于在切换分页条数, 跳转页码时, 传入最新分页信息
   * @param sortInfo 排序信息, 主要用于在点按排序按钮时, 传入最新排序信息
   */
  const requestDataTable = (
    pageInfo: PageType,
    sortInfo: SortBy,
    requestQuickValue: IQuickFilterValue = quickValue,
  ) => {
    setRecords([]); // 表格数据设置为空数组是为了显示tea组件自带的加载提示时, 不显示表格数据
    setPageInfo(pageInfo); // 在这里设置分页信息是因为在 类目和搜索筛选后要重置分页信息, 同时可以兼顾用户切换表格分页后的变化
    setSort(sortInfo);
    setQuickValue(requestQuickValue);
    updateUrlParams({
      pageInfo,
      sort: sortInfo,
      filterValue,
      keywords: props?.searchWords,
      quickValue: requestQuickValue,
      order_by: (!props?.searchWords || sortInfo?.by) ? sortInfo?.by : '',
    });

    // 上传参数
    const params: TableRequestParams = {
      game_code: props?.gameCode,
      first_labels: filterValue?.label?.includes('all') ? [] : filterValue.label,
      second_labels: filterValue?.sub_label?.includes('all') ? [] : filterValue.sub_label,
      drivers: requestQuickValue?.drivers || [],
      db_name_filters: (requestQuickValue?.db_name_filters || []).map(
        parseDbNameFilterValue,
      ),
      cycles: filterValue.cycle?.includes('all') ? [] : filterValue.cycle,
      // all_selected: props?.allSelected,
      keywords: props?.searchWords,
      order_by: (!props?.searchWords || sortInfo?.by) ? sortInfo?.by : '', // 搜索时: 如果用户主动点排序则传order_by, 否则传空
      desc: sortInfo?.order ? sortInfo?.order === 'desc' : true,
      page: pageInfo.pageIndex,
      page_size: pageInfo.pageSize,
    };
    
    runAsync(params)
      .then((res: any) => {
        setRecords(res?.data_list || []);
        setTotal(res?.row_count || 0);
      })
      .catch((e) => {
        console.log('数据表管理页面 表格请求错误: ', e);
      });
  };
  
  return (
    <ConfigProvider locale={isEn ? 'en' : 'zh'}>
      <Loading loading={runGetTableSchemeLoading}>
        <Table
          recordKey="table_id"
          // className="data-management-table"
          className={classnames(
            'data-management-table',
            className,
          )}
          columns={columns}
          records={records}
          rowDisabled={(record) => record.sync_status === 1}
          addons={[
            scrollable({
              // maxHeight: 350,
              minWidth: 1036,
            }),
            pageable({
              isPagingReset: true,
              pageSize: pageInfo.pageSize,
              pageIndex: pageInfo.pageIndex,
              pageSizeOptions: [10, 20, 50, 100],
              recordCount: total,
              stateText: dataFormatMessage('total', { total }),
              stateTextVisible: total > 10,
              pageSizeVisible: total > 10,
              pageIndexVisible: total > 10,
              jumpVisible: total > 10,
              endJumpVisible: total > 10,
              onPagingChange: (query: PagingQuery) => {
                // 请求表格数据
                requestDataTable({
                  pageIndex: query?.pageIndex || pageInfo.pageIndex,
                  pageSize: query?.pageSize || pageInfo.pageSize,
                }, sort);
              },
            }),
            sortable({
              columns: sortColumns,
              value: [sort],
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onChange: (value: SortBy[], context) => {
                requestDataTable(pageInfo, value[0] ?? {});
              },
            }),
            autotip({
              isLoading: loading,
              loadingText: commonFormatMessage('loading'),
              errorText: commonFormatMessage('load_fail'),
              isError: !!error,
              isFound: Boolean(props?.searchWords) && total === 0, // 是否显示
              foundText: commonFormatMessage('not_search'),
              clearResultText: commonFormatMessage('go_back_old'),
              onClear: () => {
                // 搜索结果为空时, 显示返回原列表按钮
                props.clearSearchWords();
              },
              style: { lineHeight: '200px' },
            }),
            // filterable({
            //   type: 'single', // todo jevin 标题小图标和重构不符合
            //   column: 'cycle',
            //   value: curCycle,
            //   onChange: (value) => {
            //     requestDataTable(pageInfo, sort, value);
            //   },
            //   // 选项列表
            //   options: cycleOptions,
            //   filterRender: (inputValue: string, option: any) => <div>11111</div>,
            // }),
            rowtooltip({
              tooltip: (record) =>
                record.sync_status === 1 ? (
                  <div style={{ padding: '10px 14px' }}>{dataFormatMessage('tooltip')}</div>
                ) : null,
            }),
          ]}
        />
        <DataTableDetail
          showTableDetail={showTableDetail}
          setShowTableDetail={setShowTableDetail}
          tableDetailId={tableDetailId}
          tableName={tableDetailName}
          gameCode={gameCode}
        />
      </Loading>
    </ConfigProvider>
  );
}

export default DataTable;
